import { useState } from 'react';
import { Content, Panel, FlexboxGrid } from 'rsuite';

import { DetailingPanelHeader } from '../DetailingPanelHeader';
import { OptionsDropdown } from '../OptionsDropdown';
import { FlexboxGridTable } from '../FlexboxGridTable';
import { ModalPriceSources } from '../ModalPriceSources';
import { LoadingSpinerLocal } from '../../../../../../components/LoadingSpinner';
import { useGetOmnichannelDetailingProducts } from '../../hooks/useGetOmnichannelDetailingProducts';
import { useOmnichannelStore } from '../../store/ModuloOmnichannel.store';
import { DETAILING_TABLE_COLUMNS, OPTIONS_DROPDOWN } from '../../constants';
import { DetailsProdutoContent, DetailsRequest, DetailsResponseItem, Paginated } from '../../types';
import * as service from '../../services';
import styles from './Detailing.module.scss';
import { useOmnichannelSources } from '../../hooks/useOmnichannelSources';
import { useNotification } from '../../../../../../hooks';

type DetailingProps = {
  loading: boolean;
};

export const Detailing = ({ loading }: DetailingProps) => {
  const [openPanelName, setOpenPanelName] = useState('');
  const [sourceHeaderData, setSourceHeaderData] = useState({});
  const omnichannelSourcesData = useOmnichannelStore(state => state.omnichannelSourcesData);
  const showModalPriceSources = useOmnichannelStore(state => state.showModalPriceSources);
  const omnichannelDetailsData = useOmnichannelStore(state => state.omnichannelDetailsData);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);
  const resetOmnichannelSourcesBodyRequest = useOmnichannelStore(state => state.resetOmnichannelSourcesBodyRequest);
  const { handleNotification } = useNotification();

  const { handleGetOmnichannelSources, isLoadingSources, handleOmnichannelSourcesPageChange } = useOmnichannelSources();
  const {
    handleOmnichannelDetailingProductsPageChange,
    handleOmnichannelDetailingProductsSortChange,
    handleOmnichannelDetailingProductsQueryChange,
    detailingProductsLoading,
  } = useGetOmnichannelDetailingProducts();

  const handleOpenPanel = (name: string) => {
    if (name === openPanelName) {
      return setOpenPanelName('');
    }
    setOpenPanelName(name);
  };

  const handleDownload = (type: 'csv' | 'xlsx') => {
    void (async () => {
      try {
        const { page, size, ...rest } = useOmnichannelStore.getState().omnichannelRequestBody;
        const data = rest;
        await service.downloadOmnichannelDetails(type, data);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        handleNotification('error', `Erro ao baixar detalhamento: ${message}`);
      }
    })();
  };

  const validateDataTable = (item: DetailsResponseItem) => {
    const { omnichannelDetailsProductsTableData } = useOmnichannelStore.getState();
    const redeName = omnichannelDetailsProductsTableData?.content?.[0]?.rede;

    const produtos = redeName === item.cabecalho.rede ? omnichannelDetailsProductsTableData : item?.produtos;

    return {
      content: produtos?.content ?? [],
      page: {
        number: produtos?.pagenumber ?? 0,
        totalElements: produtos?.page?.totalElements ?? 0,
        size: produtos?.page?.size ?? 0,
      },
    };
  };

  const handleOmnichannelDetailingProductsSources = (row: DetailsProdutoContent, rede: string) => {
    const { omnichannelRequestBody } = useOmnichannelStore.getState();
    const gtin = row?.gtin ?? '';

    setSourceHeaderData({
      productName: row?.descricao,
      productCode: row?.gtin,
    });

    const body: DetailsRequest & { sortBy: string; sortDirection: string } = {
      ...omnichannelRequestBody,
      redes: [rede],
      gtins: gtin ? [gtin] : [],
      page: 0,
      size: 10,
      sortBy: '',
      sortDirection: '',
    };
    handleGetOmnichannelSources(body);
    setOmnichannelState('omnichannelSourcesBodyRequest', body);
    setOmnichannelState('showModalPriceSources', true);
  };

  const handleCloseModalPriceSources = () => {
    setOmnichannelState('showModalPriceSources', false);
    setOmnichannelState('omnichannelSourcesData', {});
    setSourceHeaderData({});
    resetOmnichannelSourcesBodyRequest();
  };

  return (
    <Content className={styles['detailing-content']}>
      <LoadingSpinerLocal loading={loading} size="md" position="absolute" />
      <div className={styles['detailing-header']}>
        <h5 className={styles['detailing-title']}>DETALHAMENTO</h5>
        <OptionsDropdown id="detailing-more-options" data={OPTIONS_DROPDOWN} onClick={handleDownload} />
      </div>
      {omnichannelDetailsData?.length > 0 ? (
        <div className={styles['table-container']}>
          <FlexboxGrid align="middle" className={styles['table-header']}>
            <FlexboxGrid.Item colspan={9} className={styles['table-item']}>
              <div style={{ lineHeight: 1 }} className={styles['chain-title']}>
                Rede
              </div>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={5}>
              <FlexboxGrid>
                <div className={styles.channels}>Canal físico</div>
              </FlexboxGrid>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={5}>
              <div className={styles.channels}>Sites/Apps próprios</div>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={5}>
              <div className={styles.channels}>Apps de entrega</div>
            </FlexboxGrid.Item>
          </FlexboxGrid>
          {omnichannelDetailsData?.map((item: DetailsResponseItem) => (
            <Panel
              header={
                <DetailingPanelHeader
                  hasOpen={openPanelName === item.cabecalho.rede}
                  data={item}
                  onClick={() => handleOpenPanel(item.cabecalho.rede)}
                  onSearch={value => handleOmnichannelDetailingProductsQueryChange(item.cabecalho.rede, value)}
                />
              }
              key={item.cabecalho.rede}
              className={styles['panel-row']}
              bodyFill
              collapsible
              expanded={openPanelName === item.cabecalho.rede}
            >
              <FlexboxGridTable
                data={validateDataTable(item)}
                columns={DETAILING_TABLE_COLUMNS}
                loading={detailingProductsLoading}
                onPageChange={page => handleOmnichannelDetailingProductsPageChange(item.cabecalho.rede, page)}
                onSort={(column, type) => handleOmnichannelDetailingProductsSortChange(item.cabecalho.rede, column, type)}
                onClickSources={row => handleOmnichannelDetailingProductsSources(row, item.cabecalho.rede)}
              />
            </Panel>
          ))}
        </div>
      ) : null}
      <ModalPriceSources
        show={showModalPriceSources}
        priceSources={omnichannelSourcesData}
        sourceHeaderData={sourceHeaderData}
        loading={isLoadingSources}
        onClose={handleCloseModalPriceSources}
        onPageChange={handleOmnichannelSourcesPageChange}
      />
    </Content>
  );
};
